import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import moment from "moment-timezone";

import { formatDate } from "src/library/utils/moment";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { getMaxLeadTime } from "src/core/orders/utils/order-deliveries";
import {
  orderPendingPlaced,
  calculateCartTotal,
  calculatePriceTotals
} from "src/core/orders/utils/orders";
import {
  ordersApi,
  IOrder,
  IOrderLine
} from "src/core/orders/api/orders.api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import {
  ERPRJL,
  ERPSeedless
} from "src/utils/constants";
import { orderLineToGTMItem } from "src/core/products/utils/product-to-gtm-item";
import { useGTMEvent } from "src/core/shared/hooks/useGTMEvent";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import { orderReferenceNumber } from "src/core/orders/utils/order-reference-number";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { Datetime } from "src/library/components/Datetime";
import { useDrop } from "src/core/drops/hooks";
import { HtmlBody } from "src/library/components/Notes";

// TODO: if possible clear cart in header?
const OrderPlacedPage: React.ReactFCWithChildren<PageProps<unknown, {orderId: string;}>> = props => {
  const {
    match, history, terminology
  } = props;

  const gtmEvent = useGTMEvent();
  const orderId = match ? parseInt(match.params.orderId, 0) : 0;
  const [ getOrderResponse, getOrder ] = ordersApi<IOrder>("GET_ORDER");
  const [ orderState, setOrderState ] = React.useState<null | IOrder>(null);
  const [ leadTime, setLeadTime ] = React.useState<null | number>(null);
  const [ { globalSettings } ] = useGlobalSettings();
  const [ { drop } ] = useDrop();

  const {
    erp, orderStatuses, role, installedModules, displaySettings
  } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    orderStatuses: global.settings && global.settings.orderStatuses,
    role: global.role,
    installedModules: global.settings?.installedModules || [],
    displaySettings: global.settings?.displaySettings
  }));

  const orderAwaitingSubmission = React.useCallback(() => {
    if (getOrderResponse.data && orderStatuses) {
      return orderPendingPlaced(getOrderResponse.data, orderStatuses);
    }

    return false;
  }, [ getOrderResponse, orderStatuses ]);

  const priceTotals = React.useCallback((orderLines: IOrderLine[]) => {
    return calculatePriceTotals(orderLines || [], drop);
  }, [ drop ]);

  React.useEffect(() => {
    if (orderId) {
      getOrder(orderId);
    }
  // orders.api using legacy logic that causes loop when included in deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (getOrderResponse.data && globalSettings?.leadTimeGroups) {
      setOrderState(getOrderResponse.data);
      const maxLeadTime = getMaxLeadTime(getOrderResponse.data, globalSettings?.leadTimeGroups);

      if (maxLeadTime) {
        setLeadTime(maxLeadTime.defaultLeadTimeDays);
      }
      const order = getOrderResponse.data;
      const price = priceTotals(order.orderLines);

      // Track the purchase in tag manager
      gtmEvent("purchase", {
        order_origin: order.origin,
        ecommerce: {
          transaction_id: order.id.toString(),
          currency: "GBP",
          value: price.total,
          items: _.map(order.orderLines, orderLine => orderLineToGTMItem(orderLine))
        }
      });
    }
  // disabled to prevent multiple gtm events occurring
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ getOrderResponse, globalSettings?.leadTimeGroups ]);

  // Format the custom placed maessage using a template
  const customPlacedMessage = React.useMemo(() => {
    if (displaySettings?.order?.orderPlacedMessage) {
      return displaySettings.order.orderPlacedMessage
        .replace("${DeliveryDate}", formatDate(orderState?.orderDeliveryDate))
        .replace("${OrderNumber}", formatDate(orderState?.orderReference ?? orderState?.id));
    }

    return null;
  }, [
    displaySettings?.order?.orderPlacedMessage,
    orderState?.id,
    orderState?.orderDeliveryDate,
    orderState?.orderReference
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        {orderState && (
          <OrderInfoContainer>
            <Text fontSize="h1">
              Order placed
            </Text>

            <ContentContainer>
              {customPlacedMessage ?
                <HtmlBody body={customPlacedMessage} /> : (
                  <>
                    {orderState.orderDeliveryDate && (
                      <Text>
                        {`Your ${terminology.order.singular.text} will be delivered on`}

                        {" "}

                        <Text weight="bold">
                          {formatDate(orderState.orderDeliveryDate)}
                        </Text>
                        .
                      </Text>
                    )}

                    <br />

                    {erp && (erp === ERPSeedless || erp === ERPRJL) ? (
                      <Text>
                        {`Your ${terminology.order.singular.text} reference `}

                        <Text weight="bold">
                          #
                          {orderReferenceNumber(orderState, installedModules)}
                        </Text>
                      </Text>
                    ) : orderAwaitingSubmission() && typeof leadTime === "number" ? (
                      <Text>
                        {`Your ${
                          terminology.order.singular.text
                        } has been submitted successfully. You will receive an email confirmation shortly. You can come back here to view, amend or cancel your ${
                          terminology.order.singular.text
                        } at any time.`}
                      </Text>
                    ) : (
                      <Text>
                        Your
                        {" "}

                        {terminology.order.singular.text}

                        {" "}
                        has been submitted succesfully and a reference will be

                        {" "}

                        <br />

                        {" "}
                        provided to you shortly. Please
                        note this can take up to a few minutes.
                      </Text>
                    )}
                  </>
                )}
            </ContentContainer>

            {orderState.amendmentExpiryDate && moment.utc(orderState.amendmentExpiryDate).isAfter(moment.utc()) && (
              <ContentContainer>
                <ErrorNotice
                  type="warn"
                >
                  <Text>
                    {"You have until "}
                  </Text>

                  <Text weight="bold">
                    <Datetime
                      template="timedate"
                      date={orderState.amendmentExpiryDate}
                    />

                  </Text>

                  <Text>
                    {" to amend this order."}
                  </Text>
                </ErrorNotice>
              </ContentContainer>
            )}

            <ContentContainer>
              {role?.code === "van-sales-admin" ? (
                <Button onClick={() => history.push("/van-sales")}>
                  Back to dashboard
                </Button>
              ) : (
                <>
                  <Button
                    id="view-order"
                    onClick={() => history.push(`/orders/${orderState.id}`)}
                  >
                    {`View ${terminology.order.singular.text}`}
                  </Button>

                  <Button
                    onClick={() => history.push(role && role.type === "admin" ? "/admin/orders" : "/orders")}
                    $buttonType="secondary"
                  >
                    {`View all ${terminology.order.plural.text}`}
                  </Button>
                </>
              )}
            </ContentContainer>
          </OrderInfoContainer>
        )}
      </MainContentWrapper>
    </Layout>
  );
};

const OrderInfoContainer = styled.div`
  ${({ theme }) => css`
    width: 100%;
    padding: ${theme.spacing.padding.lg};
    background: white;

    h1 {
      font-size: ${theme.fonts.sizes.h1};
    }
    ${breakpoint("md", theme)} {
      width: 50%;
    }
  `}
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;

    .error-notice {
      margin-bottom: 0;
    }

    button {
      margin: ${theme.spacing.margins.md} 0;
    }
  `}
`;

export default OrderPlacedPage;
